import React, { useEffect, useState } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useNavigate, useLocation } from 'react-router-dom';
import { Box, CssBaseline, CircularProgress, Dialog, DialogTitle, DialogContent, DialogActions, Button, Typography } from '@mui/material';

import Header from './Components/Header';
import Sidebar from './Components/Sidebar';
import MainContent from './Components/MainContent';
import CreateRule from './Components/CreateRule';
import CountrySelector from './Components/CountrySelector';
import ReviewRule from './Components/ReviewRule';
import LoginPage from './Components/LoginPage';
import { LoginRequired } from './auth';
import { setNavigateFunction } from './auth/events';
import { checkCurrentUserAdmin } from './graphql';
import { handleOAuthCallback, logout as directLogout, startTokenRefreshTimer, stopTokenRefreshTimer, setSessionExpiredCallback, isAuthenticated, setSessionExpiredActive } from './auth/directOAuthHandler';
import { setOnAuthError } from './api/apolloClient';

const REQUIRED_GROUPS = [
  'App.QrRedirectAutomation.User',
  'App.QrRedirectAutomation.Approver',
  'App.QrRedirectAutomation.Prod.User',
  'App.QrRedirectAutomation.Prod.Approver'
];

// Dashboard Layout Component
const DashboardLayout: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [currentView, setCurrentView] = useState<string>('');
  const [isAdmin, setIsAdmin] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(true);

  useEffect(() => {
    const state = location.state as { view?: string } | null;
    if (state?.view) {
      setCurrentView(state.view);
    }
  }, [location.state]);

  useEffect(() => {
    const checkUserRole = async () => {
      const timeout = setTimeout(() => {
        console.warn('User role check timed out after 10 seconds');
        setIsAdmin(false);
        setLoading(false);
      }, 10000);

      try {
        const result = await checkCurrentUserAdmin();
        clearTimeout(timeout);

        const userGroups = result.groups || [];
        const hasAccess = userGroups.some((g: string) =>
          REQUIRED_GROUPS.some(rg => g.toLowerCase() === rg.toLowerCase())
        );

        if (!hasAccess) {
          navigate('/error', {
            replace: true,
            state: {
              error: 'access_denied',
              errorDescription: 'User does not have the required group membership.'
            }
          });
          return;
        }

        setIsAdmin(result.isAdmin);
        setLoading(false);
      } catch (error: any) {
        clearTimeout(timeout);
        console.error('Failed to check user role:', error);

        const isForbidden =
          error?.message?.includes('Access denied') ||
          error?.message?.includes('FORBIDDEN') ||
          error?.graphQLErrors?.some((e: any) => e.extensions?.code === 'FORBIDDEN');

        if (isForbidden) {
          navigate('/error', {
            replace: true,
            state: {
              error: 'access_denied',
              errorDescription: 'User does not have the required group membership.'
            }
          });
          return;
        }

        setIsAdmin(false);
        setLoading(false);
      }
    };

    checkUserRole();
  }, [navigate]);

  const handleViewChange = (view: string) => {
    setCurrentView(view);
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100vh', overflow: 'hidden' }}>
      <Header onLogout={directLogout} onViewChange={handleViewChange} isAdmin={isAdmin} />
      <Box sx={{ display: 'flex', flex: 1, overflow: 'hidden' }}>
        <Sidebar
          currentView={currentView}
          onViewChange={handleViewChange}
          isAdmin={isAdmin}
        />
        <Box sx={{ 
          flex: 1, 
          display: 'flex', 
          flexDirection: 'column',
          overflow: 'auto',
          transition: 'margin-left 0.25s cubic-bezier(0.4, 0, 0.2, 1)',
        }}>
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
              <CircularProgress size={40} />
            </Box>
          ) : (
            <MainContent currentView={currentView} onViewChange={handleViewChange} isAdmin={isAdmin} />
          )}
        </Box>
      </Box>
    </Box>
  );
};

// Error page wrapper that reads error from URL params or navigation state
const ErrorPage: React.FC = () => {
  const location = useLocation();
  const state = location.state as { error?: string; errorDescription?: string } | null;

  const params = new URLSearchParams(window.location.search);
  const error = params.get('error') || state?.error || null;
  const errorDescription = params.get('error_description') || state?.errorDescription || null;

  let userEmail: string | null = null;
  try {
    const userInfoStr = localStorage.getItem('user_info');
    if (userInfoStr) {
      const userInfo = JSON.parse(userInfoStr);
      userEmail = userInfo.email || null;
    }
  } catch {
    // ignore
  }

  return <LoginPage error={error} errorDescription={errorDescription} userEmail={userEmail} />;
};

// Main App Component with Routing and Auth
const App: React.FC = () => {
  const navigate = useNavigate();
  const [authCheckComplete, setAuthCheckComplete] = useState(false);
  const [showSessionExpired, setShowSessionExpired] = useState(false);
  const callbackLock = React.useRef(false);

  useEffect(() => {
    setNavigateFunction(navigate);
  }, [navigate]);

  useEffect(() => {
    const handleExpired = () => {
      setSessionExpiredActive(true);
      setShowSessionExpired(true);
    };
    setSessionExpiredCallback(handleExpired);
    setOnAuthError(handleExpired);

    if (isAuthenticated()) {
      startTokenRefreshTimer();
    }

    return () => {
      stopTokenRefreshTimer();
    };
  }, []);

  const handleSessionExpiredLogin = () => {
    setShowSessionExpired(false);
    setSessionExpiredActive(false);
    directLogout();
  };

  const [oauthError, setOauthError] = useState<{ error: string; errorDescription: string } | null>(() => {
    const urlParams = new URLSearchParams(window.location.search);
    if (urlParams.has('error')) {
      const error = urlParams.get('error') || 'unknown_error';
      const errorDescription = urlParams.get('error_description') || '';
      window.history.replaceState({}, '', window.location.pathname);
      return { error, errorDescription };
    }
    return null;
  });

  useEffect(() => {
    if (oauthError) {
      setAuthCheckComplete(true);
      return;
    }

    if (callbackLock.current) return;
    callbackLock.current = true;

    const handleCallback = async () => {
      const urlParams = new URLSearchParams(window.location.search);
      const hasOAuthCode = urlParams.has('code');

      if (!hasOAuthCode) {
        setAuthCheckComplete(true);
        return;
      }
      
      const callbackHandled = await handleOAuthCallback();
      
      if (callbackHandled) {
        startTokenRefreshTimer();
        setAuthCheckComplete(true);
        navigate('/dashboard', { replace: true });
      } else {
        setOauthError({
          error: 'login_failed',
          errorDescription: 'Authentication failed. Please try again.'
        });
        setAuthCheckComplete(true);
      }
    };
    
    handleCallback();
  }, [navigate, oauthError]);

  if (oauthError) {
    return (
      <>
        <CssBaseline />
        <LoginPage error={oauthError.error} errorDescription={oauthError.errorDescription} />
      </>
    );
  }

  if (!authCheckComplete) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: '100vh'
        }}
      >
        <CircularProgress size={60} />
      </Box>
    );
  }

  return (
    <>
      <CssBaseline />
      <Dialog
        open={showSessionExpired}
        disableEscapeKeyDown
        sx={{ '& .MuiDialog-paper': { borderRadius: 2, p: 1 } }}
      >
        <DialogTitle sx={{ fontWeight: 700 }}>Session Expired</DialogTitle>
        <DialogContent>
          <Typography>
            Your session has expired. Please log in again to continue.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button
            variant="contained"
            onClick={handleSessionExpiredLogin}
            sx={{ backgroundColor: '#2196F3', textTransform: 'none', fontWeight: 600 }}
          >
            Log In Again
          </Button>
        </DialogActions>
      </Dialog>
      <Routes>
        <Route path="/error" element={<ErrorPage />} />
        
        <Route
          path="/"
          element={
            <LoginRequired>
              <DashboardLayout />
            </LoginRequired>
          }
        />
        <Route
          path="/dashboard"
          element={
            <LoginRequired>
              <DashboardLayout />
            </LoginRequired>
          }
        />
        <Route
          path="/create-rule"
          element={
            <LoginRequired>
              <CreateRule />
            </LoginRequired>
          }
        />
        <Route
          path="/country-selector"
          element={
            <LoginRequired>
              <CountrySelector />
            </LoginRequired>
          }
        />
        <Route
          path="/review-rule"
          element={
            <LoginRequired>
              <ReviewRule />
            </LoginRequired>
          }
        />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </>
  );
};

// Root component with Router
const AppWithRouter: React.FC = () => {
  return (
    <Router>
      <App />
    </Router>
  );
};

export default AppWithRouter;
